import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar, MapPin, Filter } from "lucide-react";
import axios from "axios";
import { useFormatDate } from "../utils/dateFormat";

export default function EventListPage({ filter }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const L = (obj, field) => (lang === "el" ? (obj?.[`${field}_el`] || obj?.[field] || "") : (obj?.[field] || ""));
  const { citySlug, typeSlug } = useParams();
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const fmt = useFormatDate();

  const [filters, setFilters] = useState({
    city_id: "",
    event_type_id: "",
  });

  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const [citiesRes, typesRes] = await Promise.all([
          axios.get(`${API}/taxonomy/cities`),
          axios.get(`${API}/taxonomy/event-types`),
        ]);
        setCities(citiesRes.data);
        setEventTypes(typesRes.data);

        if (citySlug) {
          const city = citiesRes.data.find(c => c.slug === citySlug);
          if (city) setFilters(f => ({ ...f, city_id: city.id }));
        }
        if (typeSlug) {
          const type = typesRes.data.find(t => t.slug === typeSlug);
          if (type) setFilters(f => ({ ...f, event_type_id: type.id }));
        }
      } catch (error) {
        console.error("Error fetching taxonomy:", error);
      }
    };
    fetchTaxonomy();
  }, [citySlug, typeSlug]);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        let endpoint = filter === "today" ? `${API}/events/today` : `${API}/events`;
        const params = new URLSearchParams();
        if (filters.city_id) params.set("city_id", filters.city_id);
        if (filters.event_type_id) params.set("event_type_id", filters.event_type_id);
        params.set("limit", "50");

        const response = await axios.get(`${endpoint}?${params.toString()}`);
        setEvents(response.data);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [filters, filter]);

  const handleFilterChange = (key, value) => {
    setFilters(f => ({ ...f, [key]: value === "all" ? "" : value }));
  };

  const getPageTitle = () => {
    if (filter === "today") return t("events_page:tonight_title", { defaultValue: "Tonight in Cyprus" });
    if (citySlug) {
      const city = cities.find(c => c.slug === citySlug);
      return city ? `${t("events_page:events_in", { defaultValue: "Events in" })} ${L(city, "name")}` : t("events_page:title", { defaultValue: "Events" });
    }
    return t("events_page:all_events", { defaultValue: "All Events" });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-medium text-foreground tracking-tight" data-testid="event-list-title">
              {getPageTitle()}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t("events_page:count", { count: events.length })}
            </p>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4 mb-8">
            <Select value={filters.city_id || "all"} onValueChange={(v) => handleFilterChange("city_id", v)}>
              <SelectTrigger className="w-40" data-testid="event-filter-city">
                <SelectValue placeholder={t("venues_page:all_cities")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("venues_page:all_cities")}</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>{L(city, "name")}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.event_type_id || "all"} onValueChange={(v) => handleFilterChange("event_type_id", v)}>
              <SelectTrigger className="w-40" data-testid="event-filter-type">
                <SelectValue placeholder={t("venues_page:all_types")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("venues_page:all_types")}</SelectItem>
                {eventTypes.map((type) => (
                  <SelectItem key={type.id} value={type.id}>{L(type, "name")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Events Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-muted h-48 rounded-xl mb-4" />
                  <div className="bg-muted h-6 w-2/3 rounded mb-2" />
                  <div className="bg-muted h-4 w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="event-list-grid">
              {events.map((event) => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {t("events_page:no_events", { defaultValue: "No events found" })}
              </h3>
              <p className="text-muted-foreground">
                {t("events_page:no_events_sub", { defaultValue: "Check back soon for upcoming events!" })}
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function EventCard({ event }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const eventDate = new Date(event.start_datetime);
  const fmt = useFormatDate();
  const L = (field) => (lang === "el" ? (event[`${field}_el`] || event[field] || "") : (event[field] || ""));

  return (
    <Link
      to={`/event/${event.slug}`}
      className="group block bg-background rounded-xl overflow-hidden border border-border hover:border-primary hover:shadow-lg transition-all duration-300"
      data-testid={`event-card-${event.event_id}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={L("title")}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
            <Calendar className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
        {event.is_featured && (
          <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-medium">
            {t("common:featured", { defaultValue: "Featured" })}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-sm text-primary font-medium mb-2">
          <Calendar className="h-4 w-4" />
          <span>{fmt(eventDate, "EEE, MMM d 'at' h:mm a")}</span>
        </div>
        <h3 className="text-lg font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
          {L("title")}
        </h3>
        {event.venue_name && (
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {lang === "el" ? (event.venue_name_el || event.venue_name) : event.venue_name}
          </p>
        )}
        {(event.description_short || event.description_short_el) && (
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{L("description_short")}</p>
        )}
      </div>
    </Link>
  );
}
